import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Copy, Check, DollarSign, Link2, Mail, X, ExternalLink, ChevronRight, Users, CreditCard, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL || 'https://quizmind.net';
const PAYOUT_THRESHOLD = 20;

export default function AffiliatePage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [affiliate, setAffiliate] = useState(null);
  const [stats, setStats] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [paypalEmail, setPaypalEmail] = useState('');
  const [payoutLoading, setPayoutLoading] = useState(false);
  const [payoutSuccess, setPayoutSuccess] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem('affiliate_data');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setAffiliate(parsed);
        fetchStats(parsed.affiliate_id);
      } catch {
        localStorage.removeItem('affiliate_data');
      }
    }
  }, []);

  const fetchStats = async (affiliateId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/affiliates/${affiliateId}/stats`);
      if (res.ok) {
        const data = await res.json();
        setStats(data);
        setAffiliate(prev => prev ? { ...prev, balance: data.balance } : prev);
      }
    } catch (err) {
      console.error('Stats fetch error:', err);
    }
  };

  const affiliateLink = affiliate ? `${FRONTEND_URL}/?t=${affiliate.affiliate_id}` : '';
  const balance = stats?.balance ?? affiliate?.balance ?? 0;
  const canRequestPayout = balance >= PAYOUT_THRESHOLD;

  const handleRegister = async () => {
    const trimmed = email.trim();
    if (!trimmed) { toast.error('Please enter your email address'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/affiliates/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed.toLowerCase() }),
      });

      let data;
      try { data = await res.json(); }
      catch { toast.error('Server returned an unexpected response. Please try again.'); return; }

      if (!res.ok) { toast.error(data?.detail || 'Registration failed. Please try again.'); return; }

      const aff = data.affiliate;
      if (!aff || !aff.affiliate_id) { toast.error('Unexpected response from server. Please try again.'); return; }

      setAffiliate(aff);
      localStorage.setItem('affiliate_data', JSON.stringify(aff));
      fetchStats(aff.affiliate_id);
      toast.success(data.created ? 'Affiliate account created! 🎉' : 'Welcome back!');
    } catch (err) {
      console.error('Register error:', err);
      toast.error('Could not reach the server. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(affiliateLink);
    setCopied(true);
    toast.success('Link copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRequestPayout = async () => {
    if (!paypalEmail.trim()) { toast.error('Please enter your PayPal email'); return; }
    if (!canRequestPayout) {
      toast.error(`Minimum payout is $${PAYOUT_THRESHOLD}. You need $${(PAYOUT_THRESHOLD - balance).toFixed(2)} more.`);
      return;
    }
    setPayoutLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/affiliates/${affiliate.affiliate_id}/request-payout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ paypal_email: paypalEmail.trim() }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data?.detail || 'Payout request failed'); return; }
      setPayoutSuccess(true);
      toast.success(`Payout of $${balance.toFixed(2)} requested!`);
    } catch (err) {
      console.error(err);
      toast.error('Something went wrong.');
    } finally {
      setPayoutLoading(false);
    }
  };

  const handleLogout = () => {
    setAffiliate(null);
    setStats(null);
    setEmail('');
    localStorage.removeItem('affiliate_data');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-100 via-fuchsia-50 to-sky-100">
      {/* Header */}
      <header className="bg-white/70 backdrop-blur-xl border-b border-white/20 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900 leading-none">QuizMind</h1>
                <p className="text-xs text-violet-600 font-medium mt-0.5">Affiliate Program</p>
              </div>
            </a>
            {affiliate && (
              <button onClick={handleLogout} className="text-sm text-slate-500 hover:text-slate-800 transition-colors">
                Sign out
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <AnimatePresence mode="wait">

          {/* ── Registration Form ── */}
          {!affiliate && (
            <motion.div
              key="register"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 0.4 }}
              className="max-w-xl mx-auto"
            >
              <div className="text-center mb-10">
                <div className="inline-flex items-center gap-2 bg-violet-100 text-violet-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-5">
                  <DollarSign className="w-4 h-4" /> Earn 20% commission
                </div>
                <h2 className="text-4xl font-bold text-slate-900 mb-4 leading-tight">
                  Share QuizMind.<br />Earn real money.
                </h2>
                <p className="text-slate-600 text-lg leading-relaxed">
                  Get a personal affiliate link and earn <strong className="text-violet-700">20%</strong> of every quiz unlock your visitors purchase.
                </p>
              </div>

              <div className="grid grid-cols-3 gap-4 mb-10">
                {[
                  { icon: '📧', title: 'Sign up', desc: 'Enter your email to get your unique link instantly.' },
                  { icon: '🔗', title: 'Share', desc: 'Post your link on social media, blog, or email.' },
                  { icon: '💰', title: 'Earn', desc: 'Get 20% of every paid quiz unlock from your visitors.' },
                ].map((step, i) => (
                  <div key={i} className="bg-white/70 backdrop-blur-md rounded-2xl p-4 text-center border border-white/50">
                    <div className="text-2xl mb-2">{step.icon}</div>
                    <p className="font-bold text-slate-900 text-sm mb-1">{step.title}</p>
                    <p className="text-xs text-slate-500 leading-snug">{step.desc}</p>
                  </div>
                ))}
              </div>

              <div className="bg-white/80 backdrop-blur-md rounded-3xl p-8 border border-white/50 shadow-xl">
                <label className="block text-sm font-semibold text-slate-700 mb-2">Your Email Address</label>
                <div className="flex gap-3">
                  <div className="relative flex-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleRegister()}
                      placeholder="you@example.com"
                      className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-violet-500/30 text-slate-900"
                    />
                  </div>
                  <button
                    onClick={handleRegister}
                    disabled={loading}
                    className="px-6 py-3 rounded-xl bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white font-bold hover:opacity-90 transition-all disabled:opacity-60 flex items-center gap-2 shadow-lg whitespace-nowrap"
                  >
                    {loading
                      ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                      : <><span>Get Link</span><ChevronRight className="w-4 h-4" /></>
                    }
                  </button>
                </div>
                <p className="text-xs text-slate-400 mt-3 text-center">
                  Already registered? Enter the same email to access your dashboard.
                </p>
              </div>
            </motion.div>
          )}

          {/* ── Affiliate Dashboard ── */}
          {affiliate && (
            <motion.div
              key="dashboard"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 0.4 }}
            >
              <div className="text-center mb-10">
                <h2 className="text-3xl font-bold text-slate-900 mb-1">Welcome back! 👋</h2>
                <p className="text-slate-500 text-sm">{affiliate.email}</p>
              </div>

              {/* Stats row */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                <div className="bg-white/80 backdrop-blur-md rounded-2xl p-5 border border-white/50 shadow text-center">
                  <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center mx-auto mb-2">
                    <Users className="w-5 h-5 text-violet-600" />
                  </div>
                  <p className="text-2xl font-bold text-slate-900">{stats ? stats.total_tests : '—'}</p>
                  <p className="text-xs text-slate-500 mt-0.5 font-medium">Tests Completed</p>
                </div>

                <div className="bg-white/80 backdrop-blur-md rounded-2xl p-5 border border-white/50 shadow text-center">
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center mx-auto mb-2">
                    <CreditCard className="w-5 h-5 text-emerald-600" />
                  </div>
                  <p className="text-2xl font-bold text-slate-900">{stats ? stats.total_paid : '—'}</p>
                  <p className="text-xs text-slate-500 mt-0.5 font-medium">Paid Unlocks</p>
                </div>

                <div className="bg-white/80 backdrop-blur-md rounded-2xl p-5 border border-white/50 shadow text-center">
                  <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center mx-auto mb-2">
                    <DollarSign className="w-5 h-5 text-amber-600" />
                  </div>
                  <p className="text-2xl font-bold text-slate-900">${balance.toFixed(2)}</p>
                  <p className="text-xs text-slate-500 mt-0.5 font-medium">Pending Balance</p>
                </div>

                <div className="bg-white/80 backdrop-blur-md rounded-2xl p-5 border border-white/50 shadow text-center">
                  <div className="w-10 h-10 rounded-xl bg-sky-100 flex items-center justify-center mx-auto mb-2">
                    <Link2 className="w-5 h-5 text-sky-600" />
                  </div>
                  <p className="text-sm font-bold text-slate-900 font-mono tracking-widest break-all leading-tight">{affiliate.affiliate_id}</p>
                  <p className="text-xs text-slate-500 mt-1 font-medium">Your ID</p>
                </div>
              </div>

              {/* Affiliate link */}
              <div className="bg-white/80 backdrop-blur-md rounded-3xl p-6 border border-white/50 shadow-lg mb-5">
                <p className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <Link2 className="w-4 h-4 text-violet-500" /> Your Affiliate Link
                </p>
                <div className="flex gap-3 items-center">
                  <div className="flex-1 px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 font-mono text-sm text-slate-700 truncate select-all">
                    {affiliateLink}
                  </div>
                  <button
                    onClick={handleCopyLink}
                    className={`flex items-center gap-2 px-5 py-3 rounded-xl font-bold text-sm transition-all shadow-md whitespace-nowrap ${
                      copied
                        ? 'bg-emerald-500 text-white'
                        : 'bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white hover:opacity-90'
                    }`}
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <p className="text-xs text-slate-400 mt-3">
                  Share this link. You earn 20% when a visitor purchases their quiz result.
                </p>
              </div>

              {/* Payout section */}
              <div className="bg-white/80 backdrop-blur-md rounded-3xl p-6 border border-white/50 shadow-lg mb-5">
                <div className="flex items-center justify-between gap-4 mb-4">
                  <div>
                    <p className="font-semibold text-slate-800">Request a payout</p>
                    <p className="text-sm text-slate-500 mt-0.5">
                      Balance: <span className={`font-bold ${canRequestPayout ? 'text-emerald-600' : 'text-slate-700'}`}>${balance.toFixed(2)}</span>
                    </p>
                  </div>
                  <button
                    onClick={() => { setShowPayoutModal(true); setPayoutSuccess(false); setPaypalEmail(''); }}
                    disabled={!canRequestPayout}
                    className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold hover:opacity-90 transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-md text-sm whitespace-nowrap"
                  >
                    Request Payout
                  </button>
                </div>

                {/* Progress bar toward threshold */}
                {!canRequestPayout && (
                  <div>
                    <div className="flex justify-between text-xs text-slate-500 mb-1.5">
                      <span className="flex items-center gap-1">
                        <AlertCircle className="w-3 h-3 text-amber-500" />
                        Minimum payout: ${PAYOUT_THRESHOLD}
                      </span>
                      <span>${(PAYOUT_THRESHOLD - balance).toFixed(2)} to go</span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-violet-400 to-fuchsia-400 transition-all duration-700"
                        style={{ width: `${Math.min(100, (balance / PAYOUT_THRESHOLD) * 100)}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Tips */}
              <div className="bg-violet-50/80 backdrop-blur-md rounded-3xl p-6 border border-violet-100">
                <h3 className="font-bold text-slate-900 mb-3">💡 Tips for better conversions</h3>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-start gap-2"><ExternalLink className="w-4 h-4 mt-0.5 text-violet-500 shrink-0" /> Share on social media with a genuine recommendation.</li>
                  <li className="flex items-start gap-2"><ExternalLink className="w-4 h-4 mt-0.5 text-violet-500 shrink-0" /> Include in newsletters or blog posts about personality / self-discovery.</li>
                  <li className="flex items-start gap-2"><ExternalLink className="w-4 h-4 mt-0.5 text-violet-500 shrink-0" /> Mention a specific quiz that you think your audience will love.</li>
                </ul>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* ── Payout Modal ── */}
      <AnimatePresence>
        {showPayoutModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 24 }}
              className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl"
            >
              {payoutSuccess ? (
                <div className="text-center py-4">
                  <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                    <Check className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">Payout Requested!</h3>
                  <p className="text-slate-600 mb-6">
                    We received your request for <strong>${balance.toFixed(2)}</strong> to <strong>{paypalEmail}</strong>. We'll process it within 3–5 business days.
                  </p>
                  <button
                    onClick={() => setShowPayoutModal(false)}
                    className="w-full py-3 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white font-bold"
                  >
                    Close
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-2xl font-bold text-slate-900">Request Payout</h3>
                    <button onClick={() => setShowPayoutModal(false)} className="p-2 rounded-lg hover:bg-slate-100 transition-colors">
                      <X className="w-5 h-5 text-slate-500" />
                    </button>
                  </div>
                  <div className="bg-emerald-50 rounded-2xl p-4 mb-6">
                    <p className="text-sm text-slate-600">Amount to be paid</p>
                    <p className="text-3xl font-bold text-emerald-600">${balance.toFixed(2)}</p>
                  </div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Your PayPal Email</label>
                  <div className="relative mb-6">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="email"
                      value={paypalEmail}
                      onChange={(e) => setPaypalEmail(e.target.value)}
                      placeholder="paypal@example.com"
                      className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-violet-500/30 text-slate-900"
                    />
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowPayoutModal(false)}
                      className="flex-1 py-3 rounded-full font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleRequestPayout}
                      disabled={payoutLoading}
                      className="flex-1 py-3 rounded-full font-bold text-white bg-gradient-to-r from-emerald-500 to-teal-500 hover:opacity-90 transition-all shadow-lg disabled:opacity-60 flex items-center justify-center gap-2"
                    >
                      {payoutLoading
                        ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                        : 'Request Payout'
                      }
                    </button>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}